import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { FileText, Mail, Printer, Whatsapp } from '@/components/icons';
import { fetchDocHtml, type DocType } from '@/api/docs';
import { sendDocEmail, sendDocWhatsApp } from '@/api/admin';
import { apiError } from '@/lib/apiError';
import { useToast } from './useToast';

interface Props {
  type: DocType | null;
  id: number | null;
  docNo?: string;
  partyPhone?: string;
  onClose: () => void;
}

/**
 * Slide-in panel that renders the backend-rendered HTML inside an iframe (srcdoc)
 * for clean print/PDF behaviour. Toolbar: Print, WhatsApp share, Email.
 */
export function DocViewer({ type, id, docNo, partyPhone, onClose }: Props) {
  const toast = useToast();
  const open = type != null && id != null;
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Modal state
  const [emailModal, setEmailModal] = useState(false);
  const [waModal, setWaModal] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !type || !id) {
      setHtml(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDocHtml(type, id)
      .then((h) => {
        if (!cancelled) setHtml(h);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(apiError(e, 'Could not load document'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, type, id]);

  function doPrint() {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) {
      toast.push('Document not ready yet', 'warning');
      return;
    }
    // Chrome names "Save as PDF" after the page title — use the document's own
    // (GRHAMOY_<Document type>_<Doc no>) for the duration of the print dialog.
    const prevTitle = document.title;
    const docTitle = iframe.contentDocument?.title;
    if (docTitle) document.title = docTitle;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    document.title = prevTitle;
  }

  async function doSendEmail() {
    if (!type || !id || !recipient.trim()) return;
    setSending(true);
    try {
      const res = await sendDocEmail(type, id, recipient.trim());
      toast.push(res.message, 'success');
      setEmailModal(false);
      setRecipient('');
    } catch (e: unknown) {
      toast.push(apiError(e, 'Failed to send email'), 'error');
    } finally {
      setSending(false);
    }
  }

  async function doSendWhatsApp() {
    if (!type || !id || !recipient.trim()) return;
    setSending(true);
    try {
      const res = await sendDocWhatsApp(type, id, recipient.trim());
      toast.push(res.message, 'success');
      setWaModal(false);
      setRecipient('');
    } catch (e: unknown) {
      toast.push(apiError(e, 'Failed to send WhatsApp message'), 'error');
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <div
        onClick={onClose}
        className={clsx(
          'fixed inset-0 z-[2999] transition-all',
          open ? 'bg-black/55 pointer-events-auto' : 'pointer-events-none bg-transparent',
        )}
      />
      <div
        className={clsx(
          'fixed top-0 z-[3000] flex h-screen flex-col bg-[var(--bg-secondary)] shadow-[-8px_0_40px_rgba(0,0,0,0.15)]',
          'w-full max-w-[860px] border-l border-accent/20 transition-[right] duration-300',
        )}
        style={{ right: open ? 0 : '-100%' }}
      >
        {/* Toolbar */}
        <div className="flex h-14 flex-shrink-0 items-center gap-2 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-5">
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-black/[0.06] text-[var(--text-primary)] hover:bg-black/[0.1]"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="mx-1 h-7 w-px bg-white/10" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-extrabold text-[var(--text-primary)]">
              {type?.toUpperCase()} {docNo ?? ''}
            </div>
            <div className="text-[10px] font-bold text-accent">Document Preview</div>
          </div>
          <div className="flex flex-shrink-0 gap-1.5">
            <button
              onClick={() => { setRecipient(partyPhone ?? ''); setWaModal(true); }}
              disabled={!html}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-[#25d366] disabled:opacity-60"
              title="Send via WhatsApp"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.524 3.655 1.435 5.161L2 22l4.949-1.412A9.953 9.953 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.95 7.95 0 01-4.055-1.107l-.29-.173-3.007.858.862-3.012-.19-.308A7.963 7.963 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"/>
              </svg>
            </button>
            <button
              onClick={() => { setRecipient(''); setEmailModal(true); }}
              disabled={!html}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-indigo-500/90 disabled:opacity-60"
              title="Send via Email"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M2 7l10 7 10-7"/>
              </svg>
            </button>
            <button
              onClick={doPrint}
              disabled={!html}
              className="h-8 rounded-md bg-accent px-3.5 text-[11px] font-extrabold text-black disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-1"><Printer size={12} /> Print / PDF</span>
            </button>
          </div>
        </div>

        {/* Scroll area */}
        <div className="flex-1 overflow-y-auto bg-[var(--bg-primary)] p-5">
          {loading && (
            <div className="rounded-md bg-black/[0.04] p-10 text-center text-sm text-[var(--text-secondary)]">
              Loading document…
            </div>
          )}
          {error && (
            <div className="rounded-md border border-danger/40 bg-danger/[0.1] p-4 text-sm text-danger">
              {error}
            </div>
          )}
          {html && !loading && (
            <iframe
              ref={iframeRef}
              srcDoc={html}
              title={`${type}-${id}`}
              className="h-full min-h-[80vh] w-full rounded-md bg-white shadow-2xl"
            />
          )}
        </div>
      </div>

      {/* Email modal */}
      {emailModal && (
        <div className="fixed inset-0 z-[4000] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)' }}>
          <div className="w-full max-w-sm overflow-hidden rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.6)]" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            {/* Header strip */}
            <div className="flex items-center gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-500/20">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/>
                </svg>
              </div>
              <div>
                <div className="text-sm font-extrabold" style={{ color: 'var(--text-primary)' }}>Send via Email</div>
                <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Uses your configured SMTP settings</div>
              </div>
              <button onClick={() => setEmailModal(false)} className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg opacity-50 hover:opacity-100" style={{ background: 'var(--bg-soft)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {/* Body */}
            <div className="px-5 py-4">
              <div className="mb-1 rounded-lg px-3 py-2 text-[11px]" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-soft)', color: 'var(--text-muted)' }}>
                <span className="inline-flex items-center gap-1"><FileText size={12} /> <strong style={{ color: 'var(--text-secondary)' }}>{docNo ?? type?.toUpperCase()}</strong> will be sent as an HTML email to the address below.</span>
              </div>
              <label className="mb-1.5 mt-3 block text-[11px] font-bold" style={{ color: 'var(--text-secondary)' }}>
                Recipient Email Address
              </label>
              <input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doSendEmail()}
                placeholder="vendor@example.com"
                autoFocus
                className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition"
                style={{ background: 'var(--bg-card)', border: '1.5px solid var(--border-color)', color: 'var(--text-primary)' }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-color)')}
              />
            </div>
            {/* Footer */}
            <div className="flex justify-end gap-2 border-t px-5 py-3" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
              <button
                onClick={() => setEmailModal(false)}
                className="rounded-lg px-4 py-1.5 text-xs font-bold transition hover:opacity-80"
                style={{ background: 'var(--bg-soft)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
              >
                Cancel
              </button>
              <button
                onClick={doSendEmail}
                disabled={sending || !recipient.trim()}
                className="rounded-lg px-5 py-1.5 text-xs font-extrabold text-white transition disabled:opacity-40"
                style={{ background: sending ? '#4f46e5' : 'linear-gradient(135deg,#6366f1,#4f46e5)' }}
              >
                {sending ? 'Sending…' : (<span className="inline-flex items-center gap-1"><Mail size={12} /> Send Email</span>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp modal */}
      {waModal && (
        <div className="fixed inset-0 z-[4000] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)' }}>
          <div className="w-full max-w-sm overflow-hidden rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.6)]" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            {/* Header strip */}
            <div className="flex items-center gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#25d366]/20">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#25d366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.524 3.655 1.435 5.161L2 22l4.949-1.412A9.953 9.953 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.95 7.95 0 01-4.055-1.107l-.29-.173-3.007.858.862-3.012-.19-.308A7.963 7.963 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"/>
                </svg>
              </div>
              <div>
                <div className="text-sm font-extrabold" style={{ color: 'var(--text-primary)' }}>Send via WhatsApp</div>
                <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Uses your configured WhatsApp API</div>
              </div>
              <button onClick={() => setWaModal(false)} className="ml-auto flex h-7 w-7 items-center justify-center rounded-lg opacity-50 hover:opacity-100" style={{ background: 'var(--bg-soft)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {/* Body */}
            <div className="px-5 py-4">
              <div className="mb-1 rounded-lg px-3 py-2 text-[11px]" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-soft)', color: 'var(--text-muted)' }}>
                <span className="inline-flex items-center gap-1"><FileText size={12} /> <strong style={{ color: 'var(--text-secondary)' }}>{docNo ?? type?.toUpperCase()}</strong> will be shared as a WhatsApp message to the number below.</span>
              </div>
              <label className="mb-1.5 mt-3 block text-[11px] font-bold" style={{ color: 'var(--text-secondary)' }}>
                Recipient Phone (with country code)
              </label>
              <input
                type="tel"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doSendWhatsApp()}
                placeholder="+919876543210"
                autoFocus
                className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition"
                style={{ background: 'var(--bg-card)', border: '1.5px solid var(--border-color)', color: 'var(--text-primary)' }}
                onFocus={(e) => (e.currentTarget.style.borderColor = '#25d366')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-color)')}
              />
            </div>
            {/* Footer */}
            <div className="flex justify-end gap-2 border-t px-5 py-3" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}>
              <button
                onClick={() => setWaModal(false)}
                className="rounded-lg px-4 py-1.5 text-xs font-bold transition hover:opacity-80"
                style={{ background: 'var(--bg-soft)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
              >
                Cancel
              </button>
              <button
                onClick={doSendWhatsApp}
                disabled={sending || !recipient.trim()}
                className="rounded-lg px-5 py-1.5 text-xs font-extrabold text-white transition disabled:opacity-40"
                style={{ background: sending ? '#1aab52' : 'linear-gradient(135deg,#25d366,#1aab52)' }}
              >
                {sending ? 'Sending…' : <><Whatsapp size={14} className="mr-1 inline-block align-[-2px]" /> Send WhatsApp</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
